import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatPct, formatNum } from "@/lib/format";
import { defaultPeriod } from "@/lib/periods";
import { PeriodFilter } from "@/components/PeriodFilter";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Loader2 } from "lucide-react";

const Panel = ({ title, children, testid }) => (
  <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto" data-testid={testid}>
    <div className="px-5 py-4 border-b border-border"><h3 className="font-display font-semibold">{title}</h3></div>
    {children}
  </div>
);

export default function Analises() {
  const { currentOrg } = useAuth();
  const [period, setPeriod] = useState(defaultPeriod());
  const [lot, setLot] = useState([]);
  const [cat, setCat] = useState([]);
  const [pay, setPay] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: period.from, date_to: period.to };
      const [l, c, p] = await Promise.all([
        api.get("/finance/by-lot", { params }),
        api.get("/finance/by-category", { params }),
        api.get("/finance/by-payment", { params }),
      ]);
      setLot(l.data); setCat(c.data); setPay(p.data);
    } finally { setLoading(false); }
  }, [period.from, period.to]);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Análises de Resultado</h1>
          <p className="text-muted-foreground mt-1">Margem realizada por lote, categoria de peça e forma de pagamento</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
        <div className="space-y-6">
          <Panel title="Resultado por Lote" testid="analise-por-lote">
            <Table>
              <TableHeader><TableRow><TableHead>Lote</TableHead><TableHead className="text-right">Receita Líq.</TableHead><TableHead className="text-right">CMV</TableHead><TableHead className="text-right">Margem Bruta</TableHead><TableHead className="text-right">Desp. Var. (rateio)</TableHead><TableHead className="text-right">Margem Contrib.</TableHead><TableHead className="text-right">MC % (RB)</TableHead></TableRow></TableHeader>
              <TableBody>
                {lot.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sem vendas no período</TableCell></TableRow>}
                {lot.map((r) => (
                  <TableRow key={r.lot_id}>
                    <TableCell className="font-num font-semibold">{r.code}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.receita_liquida)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.cmv)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.margem_bruta)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.despesas_variaveis)}</TableCell>
                    <TableCell className="text-right font-num font-semibold">{formatBRL(r.margem_contribuicao)}</TableCell>
                    <TableCell className="text-right font-num">{formatPct(r.mc_pct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>

          <Panel title="Resultado por Categoria de Peça" testid="analise-por-categoria">
            <Table>
              <TableHeader><TableRow><TableHead>Categoria</TableHead><TableHead className="text-right">Receita Líq.</TableHead><TableHead className="text-right">CMV</TableHead><TableHead className="text-right">Margem Bruta</TableHead><TableHead className="text-right">MB % (RB)</TableHead></TableRow></TableHeader>
              <TableBody>
                {cat.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Sem vendas no período</TableCell></TableRow>}
                {cat.map((r) => (
                  <TableRow key={r.category}>
                    <TableCell className="font-medium">{r.category}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.receita_liquida)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.cmv)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.margem_bruta)}</TableCell>
                    <TableCell className="text-right font-num">{formatPct(r.mb_pct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>

          <Panel title="Faturamento por Forma de Pagamento" testid="analise-por-pagamento">
            <Table>
              <TableHeader><TableRow><TableHead>Forma de pagamento</TableHead><TableHead className="text-right">Receita Líq.</TableHead><TableHead className="text-right">Nº de itens</TableHead></TableRow></TableHeader>
              <TableBody>
                {pay.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">Sem vendas no período</TableCell></TableRow>}
                {pay.map((r) => (
                  <TableRow key={r.payment_method}>
                    <TableCell className="font-medium">{r.payment_method}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(r.receita_liquida)}</TableCell>
                    <TableCell className="text-right font-num">{formatNum(r.count)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
        </div>
      )}
    </div>
  );
}
